// This is copied wholesale from https://github.com/facebook/lexical/blob/main/packages/lexical-markdown
// However their markdwon transform is not working for our purposes. We modify exportTextFormat
// such that instead of sharing tags across nodes, it will apply tags to each node individually.

import { $getRoot, $isDecoratorNode, $isElementNode, $isLineBreakNode, $isTextNode } from "lexical";

import type {
  ElementTransformer,
  TextFormatTransformer,
  TextMatchTransformer,
  Transformer,
} from "@lexical/markdown";
import type { ElementNode, LexicalNode, TextFormatType, TextNode } from "lexical";

export function $convertToMarkdownString(transformers: Transformer[], node?: ElementNode): string {
  const exportMarkdown = createMarkdownExport(transformers);
  return exportMarkdown(node);
}

function indexBy<T>(list: T[], callback: (arg0: T) => string): Readonly<Record<string, T[]>> {
  const index: Record<string, T[]> = {};

  for (const item of list) {
    const key = callback(item);

    if (index[key]) {
      index[key].push(item);
    } else {
      index[key] = [item];
    }
  }

  return index;
}

function transformersByType(transformers: Transformer[]): Readonly<{
  element: ElementTransformer[];
  textFormat: TextFormatTransformer[];
  textMatch: TextMatchTransformer[];
}> {
  const byType = indexBy(transformers, (t) => t.type);

  return {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    element: (byType.element || []) as ElementTransformer[],
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    textFormat: (byType["text-format"] || []) as TextFormatTransformer[],
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    textMatch: (byType["text-match"] || []) as TextMatchTransformer[],
  };
}

function createMarkdownExport(transformers: Transformer[]): (node?: ElementNode) => string {
  const byType = transformersByType(transformers);

  // Export only uses text formats that are responsible for single format
  // e.g. it will filter out *** (bold, italic) and instead use separate ** and *
  const textFormatTransformers = byType.textFormat.filter(
    (transformer) => transformer.format.length === 1,
  );

  return (node) => {
    const output = [];
    const children = (node || $getRoot()).getChildren();

    for (const child of children) {
      const result = exportTopLevelElements(
        child,
        byType.element,
        textFormatTransformers,
        byType.textMatch,
      );

      if (result !== null) {
        output.push(result);
      }
    }

    return output.join("\n\n");
  };
}

function exportTopLevelElements(
  node: LexicalNode,
  elementTransformers: ElementTransformer[],
  textTransformersIndex: TextFormatTransformer[],
  textMatchTransformers: TextMatchTransformer[],
): string | null {
  for (const transformer of elementTransformers) {
    const result = transformer.export(node, (_node) =>
      exportChildren(_node, textTransformersIndex, textMatchTransformers),
    );

    if (result !== null) {
      return result;
    }
  }

  if ($isElementNode(node)) {
    return exportChildren(node, textTransformersIndex, textMatchTransformers);
  } else if ($isDecoratorNode(node)) {
    return node.getTextContent();
  } else {
    return null;
  }
}

function exportChildren(
  node: ElementNode,
  textTransformersIndex: TextFormatTransformer[],
  textMatchTransformers: TextMatchTransformer[],
): string {
  const output = [];
  const children = node.getChildren();

  mainLoop: for (const child of children) {
    for (const transformer of textMatchTransformers) {
      const result = transformer.export!(
        child,
        (parentNode) => exportChildren(parentNode, textTransformersIndex, textMatchTransformers),
        (textNode, textContent) => exportTextFormat(textNode, textContent, textTransformersIndex),
      );

      if (result !== null) {
        output.push(result);
        continue mainLoop;
      }
    }

    if ($isLineBreakNode(child)) {
      output.push("\n");
    } else if ($isTextNode(child)) {
      output.push(exportTextFormat(child, child.getTextContent(), textTransformersIndex));
    } else if ($isElementNode(child)) {
      output.push(exportChildren(child, textTransformersIndex, textMatchTransformers));
    } else if ($isDecoratorNode(child)) {
      output.push(child.getTextContent());
    }
  }

  return output.join("");
}

function exportTextFormat(
  node: TextNode,
  textContent: string,
  textTransformers: TextFormatTransformer[],
): string {
  // This function handles the case of a string looking like this: "   foo   "
  // Where it would be invalid markdown to generate: "**   foo   **"
  // We instead want to trim the whitespace out, apply formatting, and then
  // bring the whitespace back. So our returned string looks like this: "   **foo**   "
  const frozenString = textContent.trim();
  let output = frozenString;

  const applied = new Set();

  for (const transformer of textTransformers) {
    const format = transformer.format[0]!;
    const { tag } = transformer;
    const closeTag = tag.split("").reverse().join("");

    if (hasFormat(node, format) && !applied.has(format)) {
      // Multiple tags might be used for the same format (*, _)
      applied.add(format);

      output = `${tag}${output}${closeTag}`;
    }
  }

  // Replace trimmed version of textContent ensuring surrounding whitespace is not modified
  return textContent.replace(frozenString, output);
}

function hasFormat(node: LexicalNode | null | undefined, format: TextFormatType): boolean {
  return $isTextNode(node) && node.hasFormat(format);
}
